import React, { useEffect, useState, useRef } from "react";
import { arrayOf, bool, shape } from "prop-types";
import { connect, useDispatch } from "react-redux";
import { replace } from "connected-react-router";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { Col, Row } from "react-bootstrap";
import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { emailRegex, validatePhonePayway } from "util/form";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { selectors as onboardingSelectors, actions as onboardingActions } from "reducers/onboarding";
import { actions as settingsActions } from "reducers/settings";
import TextField from "pages/_components/fields/TextField";
import Button from "pages/_components/Button";
import MainContainer from "pages/_components/MainContainer";
import { ResponsiveHeader } from "pages/_components/header";
import Selector from "pages/_components/fields/formik/Selector";
import PhoneInput from "pages/_components/fields/PhoneInput";
import Counter from "pages/_components/fields/Counter";
import CustomData from "pages/_components/CustomData/CustomData";
import Notification from "pages/_components/Notification";
import { usePhoneAreaCodesArg } from "../../../hooks/usePhoneAreaCodesArg";
import PaywayTitle from "../_components/PaywayTitle";
import EnvironmentHelpSlide from "../_components/EnvironmentHelpSlide";
import EnvironmentStepper from "../_components/EnvironmentStepper";
import { useHistoricData } from "../hooks/useHistoricData";

const FILENAME_CATEGORY = "payway";
const PAGE_ID = "StepPaywayTerminals";
const FORM_ID = "payway.terminals";

const StepPaywayTerminals = ({
    isMobile,
    stateList,
    localityList,
    fetchingOnbSelectors,
    paywayTerminalsDataBackup,
    fetching,
}) => {
    const formRef = useRef(null);
    const dispatch = useDispatch();
    const [helpIsVisible, setHelpIsVisible] = useState(false);
    const [lastSelectedProvince, setLastSelectedProvince] = useState("");
    const [defaultPhoneNumber] = useState("");
    const [updatePhone, setUpdatePhone] = useState(false);
    const { argAreaCodes } = usePhoneAreaCodesArg();
    const [minTerminals, setMinTerminals] = useState(0);
    const [maxTerminals, setMaxTerminals] = useState(5);
    const [paywayTyCLink, setPaywayTyCLink] = useState(null);
    // const [checkboxState, setCheckboxState] = useState(false);
    // const [checkboxError, setCheckboxError] = useState(false);
    // const { documents } = useSelector((state) => state.settings);

    useEffect(() => {
        dispatch(environmentActions.restartEnvironmentStore());
        dispatch(environmentActions.getPaywayCategories());
        dispatch(onboardingActions.listPersonalInfoRequest(false));
        dispatch(settingsActions.loadDocumentsRequest(FILENAME_CATEGORY));

        const min = configUtils.get("payway.numberOfTerminals.min", 0);
        const max = configUtils.get("payway.numberOfTerminals.max", 5);
        const link = configUtils.get("payway.tyc.link", "https://ayuda.payway.com.ar");
        setMinTerminals(min);
        setMaxTerminals(max);
        setPaywayTyCLink(link);
    }, []);

    const noBackupData = !!paywayTerminalsDataBackup?.firstName;
    const { getBackupDataByStepId } = useHistoricData(!noBackupData);

    const onHeaderBack = () => {
        dispatch(replace("/environment/stepSelectPaywayCategory"));
        setTimeout(() => {
            getBackupDataByStepId("stepSelectPaywayCategory");
        }, 80);
    };

    const onChangeProvince = (selectedProvince) => {
        if (!!selectedProvince && lastSelectedProvince !== selectedProvince) {
            const stateId = stateList.filter((state) => state.value === selectedProvince)[0].codeId || "";
            formRef.current.setFieldValue("locality", "");
            setLastSelectedProvince(selectedProvince);
            dispatch(onboardingActions.listLocalityRequest("1", Number(stateId)));
        }
    };

    useEffect(() => {
        if (formRef && !formRef.current) {
            return;
        }
        if (paywayTerminalsDataBackup && !fetching) {
            formRef.current.setFieldValue(
                "numberOfTerminals",
                paywayTerminalsDataBackup?.numberOfTerminals || minTerminals,
            );
            formRef.current.setFieldValue("province", paywayTerminalsDataBackup?.province, "");
            if (paywayTerminalsDataBackup?.province) {
                onChangeProvince(paywayTerminalsDataBackup?.province);
            }
            formRef.current.setFieldValue("locality", paywayTerminalsDataBackup?.locality || "");
            formRef.current.setFieldValue("street", paywayTerminalsDataBackup?.street || "");
            formRef.current.setFieldValue("streetNumber", paywayTerminalsDataBackup?.streetNumber || "");
            formRef.current.setFieldValue("floor", paywayTerminalsDataBackup?.floor, "");
            formRef.current.setFieldValue("apartment", paywayTerminalsDataBackup?.apartment, "");
            formRef.current.setFieldValue("postalCode", paywayTerminalsDataBackup?.postalCode, "");
            formRef.current.setFieldValue("firstName", paywayTerminalsDataBackup?.firstName, "");
            formRef.current.setFieldValue("lastName", paywayTerminalsDataBackup?.lastName, "");
            formRef.current.setFieldValue("mail", paywayTerminalsDataBackup?.mail, "");
            formRef.current.setFieldValue("phoneNumber", paywayTerminalsDataBackup?.phoneNumber, false);
        }
    }, [fetching, paywayTerminalsDataBackup]);

    const requiredString = i18nUtils.get("forms.inputFile.massivePayments.invalid.required");

    const formikProps = {
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            numberOfTerminals: paywayTerminalsDataBackup?.numberOfTerminals || minTerminals,
            province: paywayTerminalsDataBackup?.province || "",
            locality: paywayTerminalsDataBackup?.locality || "",
            street: paywayTerminalsDataBackup?.street || "",
            streetNumber: paywayTerminalsDataBackup?.streetNumber || "",
            floor: paywayTerminalsDataBackup?.floor || "",
            apartment: paywayTerminalsDataBackup?.apartment || "",
            postalCode: paywayTerminalsDataBackup?.postalCode || "",
            firstName: paywayTerminalsDataBackup?.firstName || "",
            lastName: paywayTerminalsDataBackup?.lastName || "",
            mail: paywayTerminalsDataBackup?.mail || "",
            phoneNumber: paywayTerminalsDataBackup?.phoneNumber || "",
        },
        validationSchema: () =>
            Yup.object().shape({
                numberOfTerminals: Yup.number().required(requiredString),
                province: Yup.string().required(requiredString),
                locality: Yup.string().required(requiredString),
                street: Yup.string().required(requiredString),
                streetNumber: Yup.string().required(requiredString),
                floor: Yup.string(),
                apartment: Yup.string(),
                postalCode: Yup.string().required(requiredString),
                firstName: Yup.string().required(requiredString),
                lastName: Yup.string().required(requiredString),
                mail: Yup.string()
                    .required(i18nUtils.get("onboarding.step.verifyEmail.email.error.empty"))
                    .test(
                        "format",
                        i18nUtils.get("onboarding.step.verifyEmail.email.error.invalidFormat"),
                        (value) => !!value && emailRegex.test(value),
                    ),
                phoneNumber: validatePhonePayway(argAreaCodes),
            }),
        onSubmit: (values) => {
            dispatch(environmentActions.setPaywayTerminalsDataBackup(values));
            dispatch(environmentActions.savePaywayTerminalsData(values));
        },
    };

    const submitIsAllowed = (allFormik) => {
        const {
            numberOfTerminals,
            province,
            locality,
            street,
            streetNumber,
            postalCode,
            firstName,
            lastName,
            mail,
            phoneNumber,
        } = allFormik.values;

        return (
            !!numberOfTerminals &&
            !!province &&
            !!locality &&
            !!street &&
            !!streetNumber &&
            !!postalCode &&
            !!firstName &&
            !!lastName &&
            !!mail &&
            !!phoneNumber
        );
    };

    const onChangeLocality = (selectedLocality, setFieldValue) => {
        const locality = localityList.find((o) => o.value === selectedLocality);
        setFieldValue("locality", locality.label);
    };

    // const handleCheckboxChange = () => {
    //     setCheckboxError(false);
    //     setCheckboxState(!checkboxState);
    // };

    // const handleClickDownloadTerms = () => {
    //     dispatch(settingsActions.downloadDocumentRequest(documents[0]?.fileNameKey, FILENAME_CATEGORY));
    // };

    return (
        <>
            <ResponsiveHeader onClickBack={onHeaderBack} id="legalPerson.title" showTitle={isMobile} />
            <Notification scopeToShow="environment" />
            <MainContainer className="onboarding">
                <EnvironmentStepper isMobile={isMobile} page={PAGE_ID} />
                <div className="step-container">
                    <PaywayTitle handleHelp={() => setHelpIsVisible(true)} isDesktop={!isMobile} />
                    <div className="py-6" />
                    <Formik {...formikProps} ref={formRef}>
                        {(allFormik) => (
                            <Form>
                                <div className="box box-border-soft payway-box">
                                    <Row className="payway-terminals-form">
                                        <Col xs={12} md={4} mdOffset={1}>
                                            <Field
                                                component={Counter}
                                                idForm={FORM_ID}
                                                name="numberOfTerminals"
                                                label={`${i18nUtils.get(
                                                    "payway.terminals.number",
                                                    "¿Cuántas terminales querés?",
                                                )}`}
                                                minValue={minTerminals}
                                                maxValue={maxTerminals}
                                            />
                                        </Col>
                                        <div className="py-3" />
                                        <h2 className="payway-terminals-form-subtitle">
                                            {i18nUtils.get("payway.terminals.businessAddress", "Domicilio del negocio")}
                                        </h2>
                                        <Col xs={12} md={5} mdOffset={1}>
                                            <Field
                                                component={Selector}
                                                idForm={FORM_ID}
                                                name="province"
                                                onChange={onChangeProvince}
                                                options={stateList}
                                                label={`${i18nUtils.get(
                                                    "onboarding.personalData.province.label",
                                                    "Provincia",
                                                )}`}
                                                placeholder={`${i18nUtils.get(
                                                    "onboarding.personalData.selectorPlaceholder",
                                                    "Elegir una opción",
                                                )}`}
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={6}>
                                            <Field
                                                component={Selector}
                                                idForm={FORM_ID}
                                                name="locality"
                                                onChange={(option) => onChangeLocality(option, allFormik.setFieldValue)}
                                                options={
                                                    !localityList?.[0]?.value
                                                        ? [{ value: "1", label: "Cargando..." }]
                                                        : localityList
                                                }
                                                label={`${i18nUtils.get(
                                                    "onboarding.personalData.locality.label",
                                                    "Localidad",
                                                )}`}
                                                placeholder={`${i18nUtils.get(
                                                    "onboarding.personalData.selectorPlaceholder",
                                                    "Elegir una opción",
                                                )}`}
                                                disabled={allFormik?.values?.province === "" || fetchingOnbSelectors}
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={1}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="street"
                                                labelTextDefault={`${i18nUtils.get(
                                                    "onboarding.personalData.street.label",
                                                    "Calle",
                                                )}`}
                                                hidePlaceholder
                                                hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={6}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="streetNumber"
                                                labelTextDefault={`${i18nUtils.get(
                                                    "onboarding.personalData.streetNumber.label",
                                                    "Número",
                                                )}`}
                                                hidePlaceholder
                                                onlyNumbers
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={1}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="floor"
                                                labelTextDefault={`${i18nUtils.get(
                                                    "onboarding.personalData.floor.label",
                                                    "Piso",
                                                )}`}
                                                hidePlaceholder
                                                maxLength={2}
                                                hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={6}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="apartment"
                                                labelTextDefault={`${i18nUtils.get(
                                                    "onboarding.personalData.apartment.label",
                                                    "Departamento",
                                                )}`}
                                                hidePlaceholder
                                                hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                                maxLength={3}
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={1}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="postalCode"
                                                labelTextDefault={`${i18nUtils.get(
                                                    "onboarding.personalData.postalCode.label",
                                                    "Código postal",
                                                )}`}
                                                hidePlaceholder
                                                onlyNumbers
                                            />
                                        </Col>
                                        <div className="py-6" />
                                        <h2 className="payway-terminals-form-subtitle">
                                            {i18nUtils.get(
                                                "payway.terminals.instalationContactData",
                                                "Datos de contacto",
                                            )}
                                        </h2>
                                        <div className="py-3" />
                                        <Col xs={12} md={5} mdOffset={1}>
                                            <Field
                                                autoComplete="off"
                                                component={TextField}
                                                idForm={FORM_ID}
                                                maxLength={50}
                                                name="firstName"
                                                labelTextDefault={i18nUtils.get("payway.terminals.firstName", "Nombre")}
                                                hidePlaceholder
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={6}>
                                            <Field
                                                autoComplete="off"
                                                component={TextField}
                                                idForm={FORM_ID}
                                                maxLength={50}
                                                name="lastName"
                                                labelTextDefault={i18nUtils.get(
                                                    "payway.terminals.lastName",
                                                    "Apellido",
                                                )}
                                                hidePlaceholder
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={1}>
                                            <Field
                                                autoComplete="off"
                                                component={TextField}
                                                idForm={FORM_ID}
                                                maxLength={50}
                                                name="mail"
                                                labelTextDefault={i18nUtils.get("payway.terminals.mail", "Mail")}
                                                spaceEnabled={false}
                                                hidePlaceholder
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={6}>
                                            <Field
                                                component={PhoneInput}
                                                idForm={FORM_ID}
                                                name="phoneNumber"
                                                country="AR"
                                                mobilePhone={defaultPhoneNumber || "54"}
                                                enableSearch={false}
                                                preferredCountries={["ar"]}
                                                labelTextDefault={i18nUtils.get(
                                                    "payway.terminals.phoneNumber",
                                                    "Teléfono",
                                                )}
                                                hintLabel={i18nUtils.get(
                                                    "",
                                                    "Debes incluir el código de área y luego el número.",
                                                )}
                                                updatePhone={updatePhone}
                                                phoneAlreadyUpdated={() => setUpdatePhone(false)}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                                <div className="py-6" />
                                <div className="box info payway-terminals-alert">
                                    <CustomData
                                        icon="information_fill.svg"
                                        titleText={i18nUtils
                                            .get("payway.tyc.link.message")
                                            .replace("#{URL}", paywayTyCLink)}
                                        titleTextType="TEXT"
                                    />
                                </div>
                                <Row className="form-actions no-print">
                                    {/* <Col xs={12} md={4} mdOffset={4}>
                                        <div className="d-flex align-items-center">
                                            <div style={isIOS ? { width: "var(--space-9)" } : null}>
                                                <Check
                                                    hideLabel
                                                    name="termsAndConditios"
                                                    onChange={handleCheckboxChange}
                                                    checked={checkboxState}
                                                    error={checkboxError ? "Campo requerido" : ""}
                                                />
                                            </div>
                                            <div className="d-flex pb-4">
                                                <div
                                                    aria-hidden="true"
                                                    className={isIOS ? "whitespace-nowrap btn-link" : "btn-link"}
                                                    onClick={handleClickDownloadTerms}>
                                                    {i18nUtils.get("payway.terms.link.label")}
                                                </div>
                                            </div>
                                        </div>
                                    </Col> */}
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            className="btn-primary"
                                            label="payway.terminals.request.button.label"
                                            loading={false}
                                            type="submit"
                                            block
                                            disabled={!submitIsAllowed(allFormik)}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>

            <EnvironmentHelpSlide
                slideIsVisible={helpIsVisible}
                closeSlide={() => setHelpIsVisible(false)}
                isMobile={isMobile}
                page={PAGE_ID}
            />
        </>
    );
};

StepPaywayTerminals.propTypes = {
    isMobile: bool.isRequired,
    fetching: bool.isRequired,
    fetchingOnbSelectors: bool.isRequired,
    paywayCategories: arrayOf(shape()).isRequired,
    stateList: arrayOf(shape({})).isRequired,
    localityList: arrayOf(shape({})).isRequired,
    paywayTerminalsDataBackup: shape({}).isRequired,
};
const mapStateToProps = (state) => ({
    fetching: environmentSelectors.getFetching(state),
    fetchingOnbSelectors: onboardingSelectors.getFetching(state),
    paywayCategories: environmentSelectors.getPaywayCategories(state),
    stateList: onboardingSelectors.getStateList(state),
    localityList: onboardingSelectors.getLocalityList(state),
    paywayTerminalsDataBackup: environmentSelectors.getPaywayTerminalsDataBackup(state),
});

export default connect(mapStateToProps)(StepPaywayTerminals);
